import type { CashFlowRow, DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import type { BSRow, Period } from "@/hooks/finance/balance-sheet/useBalanceSheet";

import { buildFinancialReportRows } from "@/components/finance/reports/financial-report/buildRows";
import {
  buildFinancialReportExportRows,
  FINANCIAL_REPORT_TITLE,
} from "@/components/finance/reports/financial-report/exportExcel";
import { buildPnlRows } from "@/components/finance/reports/pnl-report/buildRows";
import {
  buildPnlExportRows,
  PNL_TITLE,
} from "@/components/finance/reports/pnl-report/exportExcel";
import {
  buildBalanceSheetExportRows,
  BS_TITLE,
} from "@/components/finance/reports/balance-sheet/exportExcel";
import type { ExcelSheetSpec } from "./exportStyledExcel";

type PeriodLabel = { key: string; label: string };

/** One currency's three report bundles (the rehydrated hook outputs). */
export type CurrencyReportBundle = {
  cashflow: { rows: CashFlowRow[]; periodLabels: PeriodLabel[]; periodKeys: string[] };
  pnl: { rows: CashFlowRow[]; periodLabels: PeriodLabel[]; periodKeys: string[] };
  balanceSheet: { rows: BSRow[]; periods: Period[] };
};

/**
 * Build the three styled report sections for one currency.
 *
 * The result satisfies both `ExcelSheetSpec` (one worksheet each) and
 * `PdfSection` (one page each — it simply ignores `sheetName`), so the combined
 * Excel and combined PDF stay in lockstep from a single definition.
 */
export function buildCurrencyReportSections(
  currency: DisplayCurrency,
  bundle: CurrencyReportBundle
): ExcelSheetSpec[] {
  const fr = buildFinancialReportRows(bundle.cashflow.rows, bundle.cashflow.periodKeys);
  const pnlBuilt = buildPnlRows(bundle.pnl.rows, bundle.pnl.periodKeys);

  return [
    {
      title: `${FINANCIAL_REPORT_TITLE} (${currency})`,
      sheetName: `Cash Flow (${currency})`,
      periodLabels: bundle.cashflow.periodLabels,
      rows: buildFinancialReportExportRows(fr.rows, fr.unmappedRows, bundle.cashflow.periodLabels),
      displayCurrency: currency,
    },
    {
      title: `${PNL_TITLE} (${currency})`,
      sheetName: `P&L (${currency})`,
      periodLabels: bundle.pnl.periodLabels,
      rows: buildPnlExportRows(pnlBuilt.rows, pnlBuilt.unmappedRows, bundle.pnl.periodLabels),
      displayCurrency: currency,
    },
    {
      title: `${BS_TITLE} (${currency})`,
      sheetName: `Balance Sheet (${currency})`,
      periodLabels: bundle.balanceSheet.periods.map((p) => ({ key: p.key, label: p.label })),
      rows: buildBalanceSheetExportRows(bundle.balanceSheet.rows, bundle.balanceSheet.periods),
      displayCurrency: currency,
      currencyDecimals: 0,
    },
  ];
}

/** ETB sections followed by USD sections — the canonical combined-export order. */
export function buildBothCurrencySections(
  etb: CurrencyReportBundle,
  usd: CurrencyReportBundle
): ExcelSheetSpec[] {
  return [
    ...buildCurrencyReportSections("ETB", etb),
    ...buildCurrencyReportSections("USD", usd),
  ];
}
